import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { API_BASE } from "@/lib/utils";
import {
  AIVORYONE_LOOP,
  COMPARE_HERO,
  COMPARE_MATRIX,
  COMPETITIVE_TAKEAWAYS,
  PROCUREMENT_ROADMAP,
} from "@/content/competitive-compare";

function statusTone(status) {
  if (status === "live") return "bg-emerald-500/15 text-emerald-700 border-emerald-500/30";
  if (status === "draft") return "bg-amber-500/15 text-amber-800 border-amber-500/30";
  return "bg-[var(--bg-elevated)] text-[var(--text-tertiary)] border-[var(--border-default)]";
}

export default function Compare() {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/pricing`, { cache: "no-store" });
        const body = await res.json().catch(() => ({}));
        if (!cancel && res.ok) setPricing(body);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const plans = Array.isArray(pricing?.plans) ? pricing.plans : [];

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base)", color: "var(--text-primary)" }}>
      <header className="border-b border-[var(--border-default)] px-6 py-5 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2">
          <Logo />
        </Link>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/trust" className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
            Trust
          </Link>
          <Link to="/signup" className="btn-primary text-sm">
            Start free trial
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-overline text-[var(--primary)]">{COMPARE_HERO.overline}</div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mt-2 max-w-4xl">{COMPARE_HERO.title}</h1>
        <p className="text-[var(--text-secondary)] mt-4 max-w-3xl text-sm leading-relaxed">{COMPARE_HERO.subtitle}</p>

        <section className="mt-12 grid md:grid-cols-3 gap-4">
          {COMPETITIVE_TAKEAWAYS.map((item) => (
            <article key={item.title} className="surface p-5">
              <h2 className="font-medium text-sm">{item.title}</h2>
              <p className="text-xs text-[var(--text-secondary)] mt-2 leading-relaxed">{item.body}</p>
            </article>
          ))}
        </section>

        <section className="mt-16">
          <div className="text-overline text-[var(--primary)]">Capability matrix</div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight mt-2">Where each platform stands</h2>
          <p className="text-xs text-[var(--text-tertiary)] mt-2 max-w-2xl">
            Snapshot for buyers evaluating AI-assisted event platforms. Cvent and GEVME capabilities summarised from public
            product messaging as of Sep 2026 — verify with each vendor for your RFP.
          </p>
          <div className="mt-6 overflow-x-auto border border-[var(--border-default)] rounded-xl">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--border-default)] bg-[var(--bg-elevated)]">
                  <th className="px-4 py-3 font-medium">Capability</th>
                  <th className="px-4 py-3 font-medium text-[var(--primary)]">AivoryEMS</th>
                  <th className="px-4 py-3 font-medium">Cvent</th>
                  <th className="px-4 py-3 font-medium">GEVME</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_MATRIX.map((row) => (
                  <tr key={row.capability} className="border-b border-[var(--border-default)] last:border-0 align-top">
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{row.capability}</td>
                    <td className="px-4 py-3 font-medium">{row.aivory}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{row.cvent}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{row.gevme}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-16">
          <div className="text-overline text-[var(--primary)]">{AIVORYONE_LOOP.overline}</div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight mt-2">{AIVORYONE_LOOP.title}</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-3 max-w-3xl leading-relaxed">{AIVORYONE_LOOP.body}</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {AIVORYONE_LOOP.stages.map((stage, idx) => (
              <div key={stage.id} className="surface p-4 relative">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <h3 className="font-medium mt-2">{stage.title}</h3>
                <p className="text-xs text-[var(--text-secondary)] mt-2 leading-relaxed">{stage.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16">
          <div className="text-overline text-[var(--primary)]">{PROCUREMENT_ROADMAP.overline}</div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight mt-2">{PROCUREMENT_ROADMAP.title}</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-3 max-w-3xl leading-relaxed">{PROCUREMENT_ROADMAP.body}</p>
          <ol className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PROCUREMENT_ROADMAP.chain.map((step, idx) => (
              <li key={step.id} className="surface p-4 flex items-start gap-3">
                <span className="font-mono text-xs text-[var(--text-tertiary)] mt-0.5">{idx + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{step.label}</span>
                    <span
                      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${statusTone(step.status)}`}
                    >
                      {step.status}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-4 text-xs text-[var(--text-tertiary)] max-w-3xl">{PROCUREMENT_ROADMAP.footnote}</p>
        </section>

        <section className="mt-16">
          <div className="text-overline text-[var(--primary)]">Plans</div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight mt-2">Transparent annual SGD pricing</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-3 max-w-2xl">
            {pricing?.billingNote ||
              "Annual SGD pricing. Includes registration, check-in, communications, and AivoryONE on Studio+."}
          </p>
          {loading ? (
            <p className="mt-8 text-sm text-[var(--text-tertiary)]">Loading plans…</p>
          ) : (
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`surface p-6 flex flex-col ${plan.featured ? "border border-[var(--primary)]" : ""}`}
                >
                  <div className="text-overline">{plan.name}</div>
                  <div className="font-heading text-3xl mt-2">{plan.price}</div>
                  <div className="text-xs text-[var(--text-tertiary)]">{plan.period}</div>
                  <p className="text-xs text-[var(--text-secondary)] mt-4 flex-1">{plan.targetCustomer || plan.body}</p>
                  <ul className="mt-4 space-y-1 text-xs text-[var(--text-tertiary)]">
                    {(plan.includes || []).slice(0, 5).map((line) => (
                      <li key={line}>· {line}</li>
                    ))}
                  </ul>
                  <Link to={plan.ctaHref || "/signup"} className="btn-secondary w-full justify-center mt-6 text-sm">
                    {plan.cta || "Learn more"}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="mt-14 text-center text-sm text-[var(--text-secondary)]">
          Evaluating for an RFP?{" "}
          <Link to="/contact?intent=sales" className="text-[var(--primary)] hover:underline">
            Talk to sales
          </Link>
          {" · "}
          <Link to="/trust" className="text-[var(--primary)] hover:underline">
            Trust Centre
          </Link>
          {" · "}
          <Link to="/signup?plan=studio" className="text-[var(--primary)] hover:underline">
            Start Studio trial
          </Link>
        </div>
      </main>
    </div>
  );
}
